
// Packages
import { defineComponent } from 'vue';

// Types
import type { Partners } from '@white-label-types/partners-api';

// Helpers
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import { ACCOUNT_URLS, DOMAINS_NAMES, getDomainUrl, goToApp } from '@white-label-helper/switch-app';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';
import { isLoungePortal } from '@white-label-helper/shared-helpers';

// Stores
import { useTravelInsuranceStore } from '@white-label-store/travel-insurance';

// Components
import ScrollTopButton from '../scroll-top-button/scroll-top-button.vue';
import IconButton from '@white-label-icon/icon-button';
import CustomerSupportContact from '../footer-customer-support-contact/footer-customer-support-contact.vue';
import TravelInsuranceDisclosureMessage from '../travel-insurance-disclosure-message/travel-insurance-disclosure-message.vue';

// Assets
import logoPropelDark from './assets/logo-propel-dark.svg';
import logoPropelLight from './assets/logo-propel-light.svg';

// Constants
import { COLOUR_SCHEMES, FEATURE_FLAGS, APP_HERO_PRODUCTS } from '@white-label-configuration/constants';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';

type ColourScheme = (typeof COLOUR_SCHEMES)[keyof typeof COLOUR_SCHEMES];

export default defineComponent({
  name: 'EcomFooter',

  components: {
    CustomerSupportContact,
    ScrollTopButton,
    IconButton,
    TravelInsuranceDisclosureMessage,
  },

  props: {
    isWlApp: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    showTravelInsuranceDisclosure(): boolean {
      const travelInsuranceStore = useTravelInsuranceStore();
      return getAppHeroProduct('lounges') === APP_HERO_PRODUCTS.TRAVEL_INSURANCE || travelInsuranceStore.shouldShowDisclosure(this.$route.name);
    },

    company(): Partners['company'] {
      return {
        name: getAppVariable('company.name'),
        address: getAppVariable('company.address'),
        registrationNumber: getAppVariable('company.registered_number'),
      };
    },

    customerSupportContact(): Partners['customer_support_contact_details'] {
      return getAppVariable('customer_support_contact_details');
    },

    receiptPages(): string[] {
      return [
        'receipt',
        'manage-booking-receipt',
        'manage-booking-cancel-receipt',
      ];
    },

    modifyPages(): string[] {
      return [
        // post-purchase pages
        'manage-booking-update-details',
        'manage-booking-cancel',
        'manage-booking-cancel-receipt',
        'manage-booking-modify',
        'manage-booking-receipt',
        'modify-checkout',
      ];
    },

    loungesPages(): string[] {
      return [
        'my-profile',
        'manage-booking',
        'manage-booking-modify',
        'manage-booking-update-details',
        'manage-booking-cancel',
        'manage-booking-cancel-receipt',
      ];
    },

    navigationLinks(): { name: string; url: string }[] {

      const {
        terms_and_conditions,
        privacy_policy,
        cookie_policy,
      }: Partners['page_texts'] = getAppVariable('page_texts');
      const language = this.$i18n.locale;
      const bookingPortalPrefix = isBookingPortalFlow() ? '/booking-portal' : '';

      const privacyPolicyUrl =
        privacy_policy.type === 'text'
          ? this.isWlApp
            ? `${window.location.origin}${bookingPortalPrefix}/privacy-policy`
            : `${getDomainUrl(DOMAINS_NAMES.ECOMMERCE)}${bookingPortalPrefix}/privacy-policy`
          : privacy_policy.value[language];

      const termsAndConditionsUrl =
        terms_and_conditions.type === 'text'
          ? this.isWlApp
            ? `${window.location.origin}${bookingPortalPrefix}/terms-and-conditions`
            : `${getDomainUrl(DOMAINS_NAMES.ECOMMERCE)}${bookingPortalPrefix}/terms-and-conditions`
          : terms_and_conditions.value[language];

      const cookiePolicyUrl =
        cookie_policy.type === 'text'
          ? this.isWlApp
            ? `${window.location.origin}${bookingPortalPrefix}/cookie-policy`
            : `${getDomainUrl(DOMAINS_NAMES.ECOMMERCE)}${bookingPortalPrefix}/cookie-policy`
          : cookie_policy.value[language];

      const accessibilityStatementUrl =
        this.isWlApp
          ? `${window.location.origin}/accessibility-statement.pdf`
          : `${getDomainUrl(DOMAINS_NAMES.ECOMMERCE)}/accessibility-statement.pdf`

      return [
        ...(getFeatureFlag(FEATURE_FLAGS.A11Y_70_ACCESSIBILITY_STATEMENT_LINK_IN_FOOTER)
            ? [{
              name: this.$t('layouts.footerParkings.links.accessibilityStatement') as string,
              url: accessibilityStatementUrl,
            }]
            : []
        ),
        {
          name: this.$t('layouts.footerParkings.links.privacyPolicy') as string,
          url: privacyPolicyUrl,
        },
        {
          name: this.$t(
            'layouts.footerParkings.links.termsAndConditions'
          ) as string,
          url: termsAndConditionsUrl,
        },
        {
          name: this.$t('layouts.footerParkings.links.cookiePolicy') as string,
          url: cookiePolicyUrl,
        },
      ].filter((item) => item.url);
    },

    showScrollTopButton(): boolean {
      return this.$route.name !== 'index' && !isLoungePortal();
    },

    showCustomerSupportContact(): boolean {
      const allowedPages = this.customerSupportContact?.allowed_pages;

      const path = this.$route.name ?? '';
      const url = process.client ? window?.location?.href : '';

      const showOnModify = !!(
        allowedPages?.modify && this.modifyPages.includes(path)
      );
      const showOnReceipt = !!(
        allowedPages?.receipt && this.receiptPages?.includes(path)
      );
      let showOnMyAccount;

      if (getAppHeroProduct('parking') === 'lounges') {
        showOnMyAccount =
          allowedPages?.account && this.loungesPages.includes(path);
      }
      if (getAppHeroProduct('parking') === 'parking') {
        showOnMyAccount =
          allowedPages?.account &&
          url.includes(getDomainUrl( DOMAINS_NAMES.ACCOUNT));
      }

      return !!(showOnModify || showOnReceipt || showOnMyAccount);
    },

    colourScheme(): ColourScheme {
      return getAppVariable('colours.footer.font', COLOUR_SCHEMES.DARK);
    },

    logoSrc(): string {
      if (this.colourScheme === COLOUR_SCHEMES.DARK) return logoPropelDark;
      else return logoPropelLight;
    },
  },

  methods: {
    showManageBookingButton(): boolean {
      return isLoungePortal() === false;
    },
    showRightBox(): boolean {
      return isLoungePortal() === false;
    },
    navigateToManageBooking(): void {
      const migrationSupport = getAppVariable('migration_support');

      if (migrationSupport?.is_in_progress) {
        window.location.href = migrationSupport.manage_booking_url;
        return;
      }

      let accountUrl: string;
      let accountUrlParams: string;

      if (this.$auth.isAuthenticated) {
        accountUrl = ACCOUNT_URLS.HOME;
      } else {
        accountUrl = ACCOUNT_URLS.SEARCH_BOOKING;
        accountUrlParams = '?action=search';
      }

      if (isBookingPortalFlow()) {
        accountUrl = '/orders';
        accountUrlParams = '?showOrdersList=true';
      }

      goToApp(
        getDomainUrl(DOMAINS_NAMES.ACCOUNT),
        accountUrl,
        accountUrlParams
      );
    },
  },
});
